import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@oxxean/types';
import { fetchSettings, updateParameter, type ParameterUpdate } from '../../lib/api/admin';
import { queryKeys } from '../../lib/query-client';
import { errorMessage } from '../../lib/errors';
import { formatDate, formatNumber } from '../../lib/format';
import { useAuth } from '../../hooks/useAuth';
import { useTableState } from '../../hooks/useTableState';
import { useToasts } from '../../hooks/useToasts';
import type { AnalyticsParameter } from '../../types/domain';
import { DataBoundary } from '../../components/DataBoundary';
import { DataTable, type Column } from '../../components/DataTable';
import { Modal } from '../../components/Modal';
import { Badge, Button, Card, Notice, Skeleton } from '../../components/primitives';
import { CheckboxField, TextAreaField, TextField } from '../../components/form';
import { PageHeading } from '../layout/PageHeading';

const SCOPE_LABEL: Record<AnalyticsParameter['scope'], string> = {
  AREA: 'Área',
  COMPANY: 'Empresa',
  GLOBAL: 'Consolidado',
};

/**
 * Configuración.
 *
 * Los umbrales de semáforo viven aquí, con vigencia temporal (DEC-A08): ningún
 * componente visual contiene un corte numérico, y cambiar un umbral no requiere
 * desplegar software.
 */
export function SettingsPage(): JSX.Element {
  const { can } = useAuth();
  const { push } = useToasts();
  const queryClient = useQueryClient();
  const canManage = can(PERMISSIONS.SETTINGS_MANAGE);
  const table = useTableState({ pageSize: 25, sortBy: 'label' });

  const [editing, setEditing] = useState<AnalyticsParameter | null>(null);

  const settingsQuery = useQuery({ queryKey: queryKeys.settings, queryFn: fetchSettings });

  const saveMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ParameterUpdate }) =>
      updateParameter(id, payload),
    onSuccess: () => {
      push({
        kind: 'success',
        title: 'Umbral actualizado',
        message: 'El cambio se aplica con vigencia desde hoy; los períodos cerrados conservan el umbral con el que se evaluaron.',
      });
      setEditing(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.settings });
    },
    onError: (error) => {
      push({ kind: 'error', title: 'No se pudo guardar el umbral', message: errorMessage(error) });
    },
  });

  const columns = useMemo<Array<Column<AnalyticsParameter>>>(
    () => [
      {
        key: 'label',
        header: 'Indicador',
        sortable: true,
        render: (row) => (
          <>
            {row.label}
            <div className="mono tiny muted">{row.key}</div>
          </>
        ),
        plain: (row) => row.label,
      },
      {
        key: 'scope',
        header: 'Nivel',
        sortable: true,
        render: (row) => <Badge tone="blue">{SCOPE_LABEL[row.scope]}</Badge>,
        plain: (row) => row.scope,
      },
      {
        key: 'greenThreshold',
        header: 'Controlado',
        align: 'right',
        render: (row) =>
          row.greenThreshold === null ? (
            <span className="muted">Sin definir</span>
          ) : (
            `${row.lowerIsBetter ? '<' : '>'} ${formatNumber(row.greenThreshold, 'decimal')}`
          ),
        plain: (row) => row.greenThreshold,
      },
      {
        key: 'yellowThreshold',
        header: 'Seguimiento',
        align: 'right',
        render: (row) =>
          row.yellowThreshold === null ? (
            <span className="muted">Sin definir</span>
          ) : (
            `${row.lowerIsBetter ? '<' : '>'} ${formatNumber(row.yellowThreshold, 'decimal')}`
          ),
        plain: (row) => row.yellowThreshold,
      },
      {
        key: 'validFrom',
        header: 'Vigente desde',
        sortable: true,
        render: (row) => formatDate(row.validFrom),
        plain: (row) => row.validFrom,
      },
      {
        key: 'validTo',
        header: 'Hasta',
        render: (row) => (row.validTo === null ? 'Vigente' : formatDate(row.validTo)),
        plain: (row) => row.validTo,
      },
      {
        key: 'pending',
        header: 'Estado',
        render: (row) =>
          row.pending ? (
            <Badge tone="amber" title="Pendiente de validación por la Gerencia de Personas">
              Provisional
            </Badge>
          ) : (
            <Badge tone="green">Validado</Badge>
          ),
        plain: (row) => (row.pending ? 'Provisional' : 'Validado'),
      },
      {
        key: 'note',
        header: 'Nota',
        render: (row) => row.note ?? '—',
        plain: (row) => row.note,
      },
    ],
    [],
  );

  const settings = settingsQuery.data;

  return (
    <>
      <PageHeading
        title="Configuración"
        description="Umbrales de semáforo, estado de las integraciones y parámetros de período. Los umbrales son una decisión de negocio y por eso viven en datos, no en código."
      />

      <DataBoundary
        isLoading={settingsQuery.isLoading}
        isError={settingsQuery.isError}
        error={settingsQuery.error}
        onRetry={() => void settingsQuery.refetch()}
        skeleton={
          <div className="stack">
            <Skeleton height={120} radius={12} />
            <Skeleton height={240} radius={12} />
          </div>
        }
      >
        {settings !== undefined && (
          <>
            <div className="row row--2e">
              <Card title="Integraciones">
                <div className="stack" style={{ gap: 9 }}>
                  {settings.integrations.length === 0 && (
                    <p className="tiny muted">No hay integraciones registradas.</p>
                  )}
                  {settings.integrations.map((integration) => (
                    <article className="sync-item" key={integration.key}>
                      <div className="sync-item__top">
                        <span className="sync-item__name">{integration.label}</span>
                        <Badge
                          tone={
                            !integration.configured
                              ? 'neutral'
                              : integration.healthy === true
                                ? 'green'
                                : integration.healthy === false
                                  ? 'red'
                                  : 'amber'
                          }
                          dot
                        >
                          {!integration.configured
                            ? 'Sin configurar'
                            : integration.healthy === true
                              ? 'Operativa'
                              : integration.healthy === false
                                ? 'Con fallas'
                                : 'Sin verificar'}
                        </Badge>
                      </div>
                      <div className="sync-item__meta">
                        <span>Destino: {integration.baseUrlMasked ?? '—'}</span>
                        <span>Última verificación: {formatDate(integration.lastCheckAt)}</span>
                      </div>
                      {integration.note !== null && (
                        <p className="tiny muted" style={{ marginTop: 6 }}>
                          {integration.note}
                        </p>
                      )}
                    </article>
                  ))}
                </div>
                <Notice tone="neutral">
                  Los secretos de integración viven únicamente en el proceso del backend. La interfaz
                  nunca los recibe, ni siquiera enmascarados a medias.
                </Notice>
              </Card>

              <Card title="Parámetros del período">
                <dl className="def-list">
                  <dt>Zona horaria de cálculo</dt>
                  <dd>{settings.timezone}</dd>
                  <dt>Carga inicial desde</dt>
                  <dd>{formatDate(settings.initialSyncFrom)}</dd>
                  <dt>Umbrales registrados</dt>
                  <dd>{formatNumber(settings.parameters.length)}</dd>
                  <dt>Provisionales</dt>
                  <dd>
                    {formatNumber(settings.parameters.filter((parameter) => parameter.pending).length)}{' '}
                    pendientes de validación
                  </dd>
                </dl>
                <Notice tone="info">
                  Los cálculos agrupan por día, mes y período usando explícitamente la zona horaria
                  indicada. Usar la zona del servidor produciría atrasos e inasistencias falsos en los
                  turnos que cruzan la medianoche.
                </Notice>
              </Card>
            </div>

            <Card title="Umbrales de semáforo" flush>
              <DataTable
                caption="Umbrales de semáforo vigentes"
                columns={columns}
                rows={settings.parameters}
                getRowId={(row) => row.id}
                state={table.state}
                onStateChange={table.setState}
                mode="client"
                searchPlaceholder="Buscar indicador…"
                emptyMessage="No hay umbrales registrados."
                rowActions={
                  canManage
                    ? (row) => (
                        <Button size="sm" variant="ghost" icon="edit" onClick={() => setEditing(row)}>
                          Editar
                        </Button>
                      )
                    : undefined
                }
              />
            </Card>
          </>
        )}
      </DataBoundary>

      {editing !== null && (
        <ParameterForm
          parameter={editing}
          saving={saveMutation.isPending}
          onCancel={() => setEditing(null)}
          onSubmit={(payload) => saveMutation.mutate({ id: editing.id, payload })}
        />
      )}
    </>
  );
}

function ParameterForm({
  parameter,
  saving,
  onCancel,
  onSubmit,
}: {
  parameter: AnalyticsParameter;
  saving: boolean;
  onCancel: () => void;
  onSubmit: (payload: ParameterUpdate) => void;
}): JSX.Element {
  const [green, setGreen] = useState(parameter.greenThreshold?.toString() ?? '');
  const [yellow, setYellow] = useState(parameter.yellowThreshold?.toString() ?? '');
  const [lowerIsBetter, setLowerIsBetter] = useState(parameter.lowerIsBetter);
  const [note, setNote] = useState(parameter.note ?? '');
  const [error, setError] = useState<string | null>(null);

  const parse = (value: string): number | null => {
    const trimmed = value.trim();
    if (trimmed.length === 0) return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : Number.NaN;
  };

  const submit = (): void => {
    const parsedGreen = parse(green);
    const parsedYellow = parse(yellow);
    if (Number.isNaN(parsedGreen) || Number.isNaN(parsedYellow)) {
      setError('Los umbrales deben ser números, o quedar vacíos si no están definidos.');
      return;
    }
    setError(null);
    onSubmit({
      greenThreshold: parsedGreen,
      yellowThreshold: parsedYellow,
      lowerIsBetter,
      note: note.trim().length === 0 ? null : note.trim(),
    });
  };

  return (
    <Modal
      open
      title={`Umbral · ${parameter.label}`}
      onClose={onCancel}
      footer={
        <>
          <Button onClick={onCancel} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="primary" loading={saving} onClick={submit}>
            Guardar
          </Button>
        </>
      }
    >
      {error !== null && (
        <Notice tone="error" role="alert">
          {error}
        </Notice>
      )}

      <Notice tone="info">
        Dejar un umbral vacío hace que el indicador se muestre <strong>sin semáforo</strong>, con el
        valor a la vista. Es preferible a un color que no significa nada.
      </Notice>

      <div className="form-grid">
        <TextField
          label="Umbral controlado"
          type="number"
          step="0.1"
          value={green}
          onChange={(event) => setGreen(event.target.value)}
        />
        <TextField
          label="Umbral seguimiento"
          type="number"
          step="0.1"
          value={yellow}
          onChange={(event) => setYellow(event.target.value)}
        />
        <div className="field form-grid__full">
          <CheckboxField
            label="Un valor menor es mejor (rotación, ausentismo)"
            checked={lowerIsBetter}
            onChange={(event) => setLowerIsBetter(event.target.checked)}
          />
        </div>
        <TextAreaField
          label="Nota"
          full
          hint="Deje constancia de quién autorizó el cambio y por qué."
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
      </div>
    </Modal>
  );
}
